/**
 * A forward-only cursor over a source buffer.
 *
 * Every read is bounds-checked and throws with the offset it failed at. A
 * DICOM file whose declared lengths run past its end is the normal shape of a
 * truncated transfer, and silently reading beyond the buffer would surface it
 * as plausible-looking pixel data rather than as an error.
 */
export class ByteCursor {
    private readonly view: DataView;
    private position: number;

    constructor(
        private readonly bytes: Uint8Array,
        startOffset = 0
    ) {
        this.view = new DataView(
            bytes.buffer,
            bytes.byteOffset,
            bytes.byteLength
        );
        this.position = startOffset;
    }

    /** The cursor's offset relative to the start of its byte range. */
    get offset(): number {
        return this.position;
    }

    get length(): number {
        return this.bytes.byteLength;
    }

    get remaining(): number {
        return this.bytes.byteLength - this.position;
    }

    get atEnd(): boolean {
        return this.position >= this.bytes.byteLength;
    }

    seek(offset: number): void {
        this.require(offset - this.position, 'seek');
        this.position = offset;
    }

    skip(count: number): void {
        this.require(count, 'skip');
        this.position += count;
    }

    private require(count: number, what: string): void {
        if (count < 0 || this.position + count > this.bytes.byteLength) {
            throw new DicomParseError(
                `${what} of ${count} byte(s) at offset ${this.position} runs past ` +
                    `the end of the ${this.bytes.byteLength}-byte buffer`,
                this.position
            );
        }
    }

    readUint16(littleEndian: boolean): number {
        this.require(2, 'read uint16');
        const value = this.view.getUint16(this.position, littleEndian);
        this.position += 2;
        return value;
    }

    readUint32(littleEndian: boolean): number {
        this.require(4, 'read uint32');
        const value = this.view.getUint32(this.position, littleEndian);
        this.position += 4;
        return value;
    }

    /** Reads `count` bytes as a zero-copy view over the source buffer. */
    readBytes(count: number): Uint8Array {
        this.require(count, 'read');
        const slice = this.bytes.subarray(this.position, this.position + count);
        this.position += count;
        return slice;
    }

    /** Reads `count` bytes as ASCII without advancing past the buffer's end. */
    readAscii(count: number): string {
        const slice = this.readBytes(count);
        let text = '';
        for (let i = 0; i < slice.length; i++) {
            text += String.fromCharCode(slice[i]);
        }
        return text;
    }

    /** A zero-copy view over a range, without moving the cursor. */
    peekBytes(offset: number, count: number): Uint8Array {
        if (offset < 0 || offset + count > this.bytes.byteLength) {
            throw new DicomParseError(
                `peek of ${count} byte(s) at offset ${offset} runs past the end ` +
                    `of the ${this.bytes.byteLength}-byte buffer`,
                offset
            );
        }
        return this.bytes.subarray(offset, offset + count);
    }
}

/** A DICOM stream that does not conform where the parser needs it to. */
export class DicomParseError extends Error {
    readonly offset: number;

    constructor(message: string, offset: number) {
        super(message);
        this.name = 'DicomParseError';
        this.offset = offset;
    }
}
